import type { QueryKey } from '@tanstack/react-query';
import {
  useController,
  type FieldValues,
  type Path,
  type PathValue,
  type UseControllerProps,
  type UseControllerReturn,
} from 'react-hook-form';
import { SelectAsync, type Item } from './SelectAsync.tsx';

type Props<
  T extends FieldValues,
  N extends Path<T>,
  TQueryData,
  TQueryError extends Error,
  TQueryKey extends QueryKey,
> = {
  renderOptions: ({
    onChange,
    items,
  }: {
    onChange: UseControllerReturn['field']['onChange'];
    items: Item[];
  }) => NonNullable<React.ReactElement>;
  renderValue?: (val: PathValue<T, N>) => string | null;
} & Omit<
  React.ComponentPropsWithoutRef<typeof SelectAsync<PathValue<T, N>, TQueryData, TQueryError, TQueryKey>>,
  'children' | 'renderValue' | 'value'
> &
  UseControllerProps<T, N>;

export function SelectAsyncControlled<
  T extends FieldValues,
  N extends Path<T>,
  TQueryData,
  TQueryError extends Error,
  TQueryKey extends QueryKey,
>({ control, rules, name, renderOptions, ...props }: Props<T, N, TQueryData, TQueryError, TQueryKey>) {
  const {
    field: { ref, onChange, value, onBlur },
  } = useController({
    name,
    control,
    rules,
  });

  return (
    <SelectAsync
      onBlur={onBlur}
      innerRef={ref}
      defaultValue={value ?? undefined}
      value={value}
      onChange={onChange}
      {...props}
    >
      {(items) => renderOptions({ onChange, items })}
    </SelectAsync>
  );
}
